/**
 * Helpers for rendering soft-deleted ("tombstoned") entities on read
 * surfaces. Lookup lists fetched with `includeDeleted` carry deleted entities
 * (so old recordings can still show their names); surfaces use these helpers
 * to gray them out and append a localized "(deleted)" marker. Pickers never
 * build their option lists from those lookups; the only tombstone a picker
 * shows is the currently-assigned entity in edit forms (resolved via the
 * detail endpoint, rendered disabled — see `showUnselectableValue`).
 *
 * Non-active users (suspended/inactive/pending) get only a name suffix —
 * never the gray and never the unselectability: such an account is still a
 * real, pickable person, because the work it is referenced for still has to
 * be named. The suffix renders muted via the `UserName` component; these
 * string helpers serve the contexts that genuinely need plain text (native
 * tooltips, sortable labels).
 */ const SUFFIXED_STATUSES = [
    "suspended",
    "inactive",
    "pending"
];
export function resolveUserLifecycle(user) {
    if (user?.deletedAt) return "deleted";
    const status = user?.status;
    return status && SUFFIXED_STATUSES.includes(status) ? status : null;
}
const LIFECYCLE_SUFFIX_KEY = {
    deleted: "common.deleted",
    suspended: "userStatus.suspended",
    inactive: "userStatus.inactive",
    pending: "userStatus.pending"
};
/**
 * The localized " (deleted)" / " (suspended)" / … marker, "" for a live
 * user. Pure — callers supply `t` so it stays usable from both React
 * components and plain builder code.
 */ export function formatUserLifecycleSuffix(lifecycle, t) {
    return lifecycle ? ` (${t(LIFECYCLE_SUFFIX_KEY[lifecycle])})` : "";
}
/**
 * Whether an entity is a soft-deleted tombstone. Structural parameter so both
 * populated refs and plain contract entities (User/Team/System/
 * ExternalContact) qualify.
 */ export function isAssigneeDeleted(entity) {
    return Boolean(entity?.deletedAt);
}
/**
 * Build a {@link TombstoneLookup} from an includeDeleted lookup list.
 * Accepts `undefined` so callers can pass query data before it resolves.
 */ export function buildTombstoneLookup(entities) {
    return new Map((entities ?? []).map((e)=>[
            e._id,
            {
                deletedAt: e.deletedAt,
                status: e.status
            }
        ]));
}
/**
 * Whether the entity behind `id` is soft-deleted according to a lookup.
 * Null-safe on both sides: a missing id or an unresolved lookup renders the
 * live appearance.
 */ export function isDeletedInLookup(id, lookup) {
    return Boolean(id && lookup?.get(id)?.deletedAt);
}
/**
 * Tailwind classes graying out a soft-deleted entity's card/row. The single
 * canonical literal — every deleted-entity surface (cards, sidebar rows,
 * swimlane labels) applies this instead of hand-rolling opacity classes.
 */ export const DELETED_ENTITY_CLASS = "opacity-60 grayscale";
/**
 * Append the localized " (deleted)" tombstone marker to a display name.
 * Pure — callers supply `t` so the helper stays usable from both React
 * components (react-i18next) and plain builder code. Returns the name
 * unchanged for live entities so call sites can apply it unconditionally.
 */ export function appendDeletedSuffix(name, isDeleted, t) {
    return isDeleted ? `${name} (${t("common.deleted")})` : name;
}
/**
 * Append the one lifecycle marker a user's state earns
 * ({@link resolveUserLifecycle} owns the precedence). Plain-string variant
 * for non-JSX contexts; React surfaces render the muted-tone equivalent via
 * the `UserName` component instead.
 */ export function appendUserStatusSuffix(name, user, t) {
    return `${name}${formatUserLifecycleSuffix(resolveUserLifecycle(user), t)}`;
}
/**
 * Deleted check for a *backend-populated* user by id. The backend's user
 * `toJSON` strips `deletedAt` from every serialized user, so populated user
 * objects (recordedBy, helpers.persons, who-else entries, process owner)
 * never carry the tombstone — the lookup (fetched with `includeDeleted`) is
 * the only place it survives.
 *
 * Structurally typed so any `AssigneeLookup`-shaped object qualifies without
 * this module depending on the process-flow builder.
 */ export function isUserDeletedInLookup(id, lookup) {
    return isDeletedInLookup(id, lookup?.users);
}

//# sourceMappingURL=deleted-entity-utils.js.map